import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import type { AlaCarteContent } from '@core/interfaces/api';
import { AlaCarteTemplate } from '../templates/alacarte-template';
import {
  useAlaCarteMenusQuery,
  useCreateAlaCarteMenuMutation,
  useDeleteAlaCarteMenuMutation,
  useUpdateAlaCarteMenuMutation,
} from '../../queries/alacarte.queries';
import { EMPTY_CONTENT, normalizeContent } from './alacarte-helpers';
import { NewMenuDialog, SaveAsDialog, WysiwygPage } from './alacarte-dialogs';

type ColumnLayout = NonNullable<AlaCarteContent['columnLayout']>;

export interface AlaCarteMenuBuilderProps {
  siteId: number;
  communityName: string;
  bugImageUrl: string | null;
  onPrintReady?: (fn: () => void) => void;
}

export function AlaCarteMenuBuilder({
  siteId,
  communityName,
  bugImageUrl,
  onPrintReady,
}: AlaCarteMenuBuilderProps) {
  const [selectedMenuId, setSelectedMenuId] = useState<number | null>(null);
  const [title, setTitle] = useState('\u00c0 LA CARTE MENU');
  const [content, setContent] = useState<AlaCarteContent>(EMPTY_CONTENT);
  const [isDirty, setIsDirty] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const printRef = useRef<HTMLDivElement>(null);
  const saveAnchorRef = useRef<HTMLDivElement>(null);

  const { data: menus = [] } = useAlaCarteMenusQuery({ siteId });
  const createMutation = useCreateAlaCarteMenuMutation();
  const updateMutation = useUpdateAlaCarteMenuMutation(siteId);
  const deleteMutation = useDeleteAlaCarteMenuMutation(siteId);

  const loadMenu = (id: number | null) => {
    setSelectedMenuId(id);
    if (id === null) {
      setTitle('\u00c0 LA CARTE MENU');
      setContent(EMPTY_CONTENT);
      setIsDirty(false);
      return;
    }
    const menu = menus.find((m) => m.id === id);
    if (menu) {
      setTitle(menu.title);
      setContent(normalizeContent(menu.content));
      setIsDirty(false);
    }
  };

  const handleContentChange = (c: AlaCarteContent) => {
    setContent(c);
    setIsDirty(true);
  };

  const handleTitleChange = (t: string) => {
    setTitle(t);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (selectedMenuId !== null) {
      await updateMutation.mutateAsync({ id: selectedMenuId, data: { title, content } });
    } else {
      const created = await createMutation.mutateAsync({ siteId, title, content });
      setSelectedMenuId(created.id);
    }
    setIsDirty(false);
  };

  const handleSaveAs = async (newTitle: string) => {
    const created = await createMutation.mutateAsync({ siteId, title: newTitle, content });
    setSelectedMenuId(created.id);
    setTitle(newTitle);
    setIsDirty(false);
  };

  const handleCreate = async (newTitle: string, newContent: AlaCarteContent) => {
    const normalized = normalizeContent(newContent);
    const created = await createMutation.mutateAsync({ siteId, title: newTitle, content: normalized });
    setSelectedMenuId(created.id);
    setTitle(created.title);
    setContent(normalized);
    setIsDirty(false);
  };

  const handlePrint = useCallback(() => {
    setMode('preview');
    // Inject a print stylesheet that hides everything except the preview
    const styleId = 'alacarte-print-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @media print {
          /* Hide everything by default */
          body > *:not(.MuiDialog-root),
          .MuiDialog-root .MuiDialogTitle-root,
          .MuiDialog-root .MuiDialogActions-root { display: none !important; }
          /* Reset dialog and all intermediate containers to fill page */
          .MuiDialog-root,
          .MuiDialog-root .MuiDialog-container,
          .MuiDialog-root .MuiPaper-root,
          .MuiDialog-root .MuiDialogContent-root,
          .MuiDialog-root .MuiDialogContent-root > *,
          .MuiDialog-root .MuiDialogContent-root > * > * {
            position: static !important; display: block !important; overflow: visible !important;
            max-height: none !important; height: auto !important; padding: 0 !important; margin: 0 !important;
            background: #fff !important; box-shadow: none !important; border-radius: 0 !important;
            max-width: none !important; width: auto !important; border: none !important;
          }
          .MuiBackdrop-root { display: none !important; }
          @page { size: letter; margin: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    // Wait for preview to render, then trigger print
    setTimeout(() => window.print(), 300);
  }, []);

  // Clean up injected print stylesheet on unmount
  useEffect(() => {
    return () => {
      document.getElementById('alacarte-print-style')?.remove();
    };
  }, []);

  // Expose print function to parent
  useEffect(() => {
    onPrintReady?.(handlePrint);
  }, [handlePrint, onPrintReady]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar — hidden when printing */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, '@media print': { display: 'none' } }}>
        <Select
          value={selectedMenuId ?? ''}
          onChange={(e) => loadMenu(e.target.value === '' ? null : Number(e.target.value))}
          size="small"
          displayEmpty
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">— Select a saved menu —</MenuItem>
          {menus.map((m) => (
            <MenuItem key={m.id} value={m.id}>{m.title}</MenuItem>
          ))}
        </Select>
        <Button size="small" variant="outlined" onClick={() => setShowNewDialog(true)}>
          New
        </Button>
        {selectedMenuId !== null && (
          <Tooltip title="Delete menu">
            <IconButton
              size="small"
              onClick={() => setShowDeleteDialog(true)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box ref={saveAnchorRef} sx={{ display: 'inline-flex' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SaveIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={() => setSaveMenuOpen((prev) => !prev)}
          >
            Save
          </Button>
        </Box>
        <Popper open={saveMenuOpen} anchorEl={saveAnchorRef.current} transition disablePortal placement="bottom-start" sx={{ zIndex: 1300 }}>
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper elevation={4}>
                <ClickAwayListener onClickAway={() => setSaveMenuOpen(false)}>
                  <MenuList dense>
                    <MenuItem
                      onClick={() => { setSaveMenuOpen(false); handleSave(); }}
                      disabled={!isDirty || createMutation.isPending || updateMutation.isPending}
                    >
                      Save
                    </MenuItem>
                    <MenuItem onClick={() => { setSaveMenuOpen(false); setShowSaveAsDialog(true); }}>
                      Save As…
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        <Box sx={{ flex: 1 }} />
        {mode === 'edit' && (
          <ToggleButtonGroup
            value={content.columnLayout ?? '1/3-2/3'}
            exclusive
            size="small"
            onChange={(_, val) => {
              if (val) handleContentChange({ ...content, columnLayout: val as ColumnLayout });
            }}
          >
            <ToggleButton value="1/3-2/3" sx={{ textTransform: 'none', fontSize: '12px', py: 0.25 }}>⅓ – ⅔</ToggleButton>
            <ToggleButton value="1/2-1/2" sx={{ textTransform: 'none', fontSize: '12px', py: 0.25 }}>½ – ½</ToggleButton>
            <ToggleButton value="2/3-1/3" sx={{ textTransform: 'none', fontSize: '12px', py: 0.25 }}>⅔ – ⅓</ToggleButton>
          </ToggleButtonGroup>
        )}
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) setMode(val); }}
        >
          <ToggleButton value="edit" sx={{ textTransform: 'none', fontSize: '12px', py: 0.25, gap: 0.5 }}>
            <EditIcon fontSize="small" /> Edit
          </ToggleButton>
          <ToggleButton value="preview" sx={{ textTransform: 'none', fontSize: '12px', py: 0.25, gap: 0.5 }}>
            <VisibilityIcon fontSize="small" /> Preview
          </ToggleButton>
        </ToggleButtonGroup>
        {mode === 'preview' && (
          <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print
          </Button>
        )}
      </Box>

      {/* Canvas */}
      <Box
        ref={printRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: '#888',
          p: 2,
          '@media print': {
            bgcolor: '#fff',
            p: 0,
            overflow: 'visible',
          },
        }}
      >
        {mode === 'edit' ? (
          <WysiwygPage
            title={title}
            bugImageUrl={bugImageUrl}
            content={content}
            onTitleChange={handleTitleChange}
            onContentChange={handleContentChange}
          />
        ) : (
          <AlaCarteTemplate
            communityName={communityName}
            title={title}
            bugImageUrl={bugImageUrl}
            content={content}
          />
        )}
      </Box>

      <NewMenuDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreate={handleCreate}
        existingMenus={menus}
      />

      <SaveAsDialog
        open={showSaveAsDialog}
        onClose={() => setShowSaveAsDialog(false)}
        onSave={handleSaveAs}
        defaultTitle={title ? `${title} (copy)` : 'Untitled Menu'}
      />

      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Menu</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this menu? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (selectedMenuId !== null) {
                await deleteMutation.mutateAsync(selectedMenuId);
                loadMenu(null);
              }
              setShowDeleteDialog(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
